import { useEffect, useState } from 'react';
import { SHIPPING_METHODS, SHIPPING_LABELS } from '../../utils/constants';
import { shippingService } from '../../services/shippingService';
import { formatPrice } from '../../utils/helpers';
import './ShippingMethod.css';

const METHODS = [SHIPPING_METHODS.STANDARD, SHIPPING_METHODS.EXPRESS];

const ShippingMethod = ({
  selectedMethod,
  onSelect,
  districtId,
  subtotal = 0,
  paymentMethod = null,
}) => {
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!districtId) {
      setQuotes({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all(
      METHODS.map((method) =>
        shippingService
          .getQuote({ districtId, method, subtotal, paymentMethod })
          .then((q) => [method, q])
      )
    )
      .then((entries) => {
        if (cancelled) return;
        setQuotes(Object.fromEntries(entries));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load shipping rates');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [districtId, subtotal, paymentMethod]);

  if (!districtId) {
    return (
      <div className="shipping-method">
        <h3>Shipping Method</h3>
        <p className="shipping-empty">Pick a delivery district to see rates.</p>
      </div>
    );
  }

  return (
    <div className="shipping-method">
      <h3>Shipping Method</h3>
      {loading && <p className="shipping-empty">Loading rates…</p>}
      {error && <p className="shipping-error">{error}</p>}

      <div className="shipping-options">
        {METHODS.map((method) => {
          const quote = quotes[method];
          const isSelected = selectedMethod === method;
          const free = quote?.free_shipping_applied;
          const cost = quote?.shipping_cost ?? 0;
          const eta = quote
            ? quote.eta_days_min === quote.eta_days_max
              ? `${quote.eta_days_min} day${quote.eta_days_min === 1 ? '' : 's'}`
              : `${quote.eta_days_min}-${quote.eta_days_max} days`
            : null;

          return (
            <label
              key={method}
              className={`shipping-option ${isSelected ? 'selected' : ''}`}
            >
              <input
                type="radio"
                name="shippingMethod"
                value={method}
                checked={isSelected}
                onChange={(e) => onSelect(e.target.value)}
              />
              <div className="shipping-option-content">
                <div className="shipping-option-main">
                  <span className="shipping-option-label">{SHIPPING_LABELS[method]}</span>
                  {eta && <span className="shipping-option-eta">Delivers in {eta}</span>}
                </div>
                <span className="shipping-option-price">
                  {free ? (
                    <span className="shipping-free">FREE</span>
                  ) : (
                    formatPrice(cost)
                  )}
                </span>
              </div>
            </label>
          );
        })}
      </div>

      {quotes[selectedMethod]?.zone?.name && (
        <p className="shipping-zone-note">
          Delivery zone: <strong>{quotes[selectedMethod].zone.name}</strong>
          {quotes[selectedMethod].free_shipping_applied &&
            ' • Free shipping unlocked!'}
        </p>
      )}
    </div>
  );
};

export default ShippingMethod;
